/* Native fixed bundles; public, read-only Storefront API. No token or stored list. */
(() => {
  if (customElements.get('mbi-bundle-contents')) return;
  const query = `query BundleContents($id: ID!, $country: CountryCode!, $after: String)
    @inContext(country: $country) {
    node(id: $id) { ... on ProductVariant { id
      components(first: 50, after: $after) {
        nodes { quantity productVariant { id title
          price { amount currencyCode }
          image { url altText width height }
          product { title handle onlineStoreUrl }
        } }
        pageInfo { hasNextPage endCursor }
      }
    } }
  }`;

  customElements.define('mbi-bundle-contents', class extends HTMLElement {
    connectedCallback() {
      this.toggle = this.querySelector('.mbi-bundle__toggle');
      this.onToggle = () => {
        const open = this.toggle.getAttribute('aria-expanded') !== 'true';
        this.toggle.setAttribute('aria-expanded', String(open));
        this.querySelector('ul').hidden = !open;
        this.classList.toggle('is-open', open);
      };
      this.toggle.addEventListener('click', this.onToggle);
      this.scope = this.closest('.shopify-section') || this.parentElement;
      this.onChange = (event) => {
        // Align dispatches this after synchronising the selected variant.
        if (event.target.matches('input[name="id"]')) this.load(event.target.value);
      };
      this.onPopState = () => this.load(new URL(location.href).searchParams.get('variant') || this.dataset.variantId);
      this.scope.addEventListener('change', this.onChange);
      window.addEventListener('popstate', this.onPopState);
      this.load(this.dataset.variantId);
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.onToggle);
      this.currentId = null;
      this.controller?.abort();
      this.scope?.removeEventListener('change', this.onChange);
      window.removeEventListener('popstate', this.onPopState);
    }

    async load(id) {
      if (!/^\d+$/.test(String(id)) || this.currentId === String(id)) return;
      this.currentId = String(id);
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.hidden = true;
      const list = this.querySelector('ul');
      list.replaceChildren();
      const timeout = setTimeout(() => controller.abort(), 10000);
      try {
        const items = [];
        let after = null;
        do {
          const response = await fetch('/api/2026-10/graphql.json', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({ query, variables: {
              id: `gid://shopify/ProductVariant/${id}`,
              country: this.dataset.country, after
            } })
          });
          if (!response.ok) throw new Error('Bundle request failed');
          const result = await response.json();
          if (result.errors?.length) throw new Error('Bundle query failed');
          const components = result.data?.node?.components;
          if (!components) return;
          items.push(...components.nodes);
          after = components.pageInfo.hasNextPage ? components.pageInfo.endCursor : null;
        } while (after);
        if (controller.signal.aborted || this.currentId !== String(id) || !this.isConnected) return;
        const fragment = document.createDocumentFragment();
        for (const item of items) {
          const variant = item.productVariant;
          if (!variant?.product) continue;
          const product = variant.product;
          const li = document.createElement('li');
          const link = document.createElement(product.onlineStoreUrl ? 'a' : 'div');
          link.className = 'mbi-bundle__item';
          if (product.onlineStoreUrl) {
            const root = this.dataset.root.replace(/\/?$/, '/');
            link.href = `${root}products/${encodeURIComponent(product.handle)}?variant=${variant.id.split('/').pop()}`;
          }
          if (variant.image) {
            const image = document.createElement('img');
            const url = new URL(variant.image.url);
            url.searchParams.set('width', '160');
            image.src = url.href;
            image.alt = ''; // Linked title provides the accessible name.
            image.width = 80;
            image.height = 80;
            image.loading = 'lazy';
            link.append(image);
          }
          const text = document.createElement('span');
          text.className = 'mbi-bundle__text';
          const title = document.createElement('strong');
          title.textContent = product.title;
          text.append(title);
          if (variant.title !== 'Default Title') {
            const option = document.createElement('span');
            option.textContent = variant.title;
            text.append(option);
          }
          const quantity = document.createElement('span');
          quantity.className = 'mbi-bundle__quantity';
          quantity.textContent = `${this.dataset.quantityLabel}: ${item.quantity}`;
          text.append(quantity);
          if (variant.price) {
            const price = document.createElement('span');
            price.className = 'mbi-bundle__price';
            const formatted = new Intl.NumberFormat(this.dataset.locale || 'en-GB', {
              style: 'currency', currency: variant.price.currencyCode
            }).format(Number(variant.price.amount));
            price.textContent = this.dataset.priceLabel.replace('[price]', formatted);
            text.append(price);
          }
          link.append(text);
          li.append(link);
          fragment.append(li);
        }
        if (!fragment.childNodes.length) return;
        list.replaceChildren(fragment);
        this.hidden = false;
      } catch (error) {
        // Optional merchandising content must never block purchasing.
        if (error.name !== 'AbortError') console.warn('Bundle contents could not be loaded.');
      } finally {
        clearTimeout(timeout);
      }
    }
  });
})();
